import type { useDialogs } from 'hooks/dialogs'
import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import type { useTranslationTyped } from 'i18n/i18n'
import type { DatePickerDialog } from 'components/design-system/BreakoutDateTimeInput'
import { toast } from 'react-hot-toast'
import { getRoomStateScheduledAtSchema } from '../roomStateScheduledAtSchema'
import type { DateTime } from 'luxon'
import { captureException } from '@sentry/react'

export const showScheduleRoomDatePickerDialog = ({
  showDatePickerDialog,
  popDialog,
  tt,
  cubit,
}: {
  popDialog: ReturnType<typeof useDialogs>['popDialog']
  showDatePickerDialog: (
    props: React.ComponentProps<typeof DatePickerDialog>
  ) => void
  tt: ReturnType<typeof useTranslationTyped>['tt']
  cubit: StudentAssignmentCubit
}) => {
  const props = getScheduleRoomStateDatePickerDialogProps({
    useInlineDialog: false,
    // Use libraryObject.scheduledAtDate (not roomState) so an incomplete
    // (mustScheduleSuspended) session clears the picker instead of pre-filling
    // the stale past date, which would fail validation on Save. See BL-4399.
    scheduledAtDate: cubit.libraryObject.scheduledAtDate,
    roomStateName: cubit.roomState.roomStateName,
    isOpen: false, // no op for as this is never inline
    minDate: cubit.effectiveMinScheduleDate,
    maxDate: cubit.effectiveExpiresAtDate,
    cubit,
    schema: getRoomStateScheduledAtSchema({
      tt,
      expiresAt: cubit.effectiveExpiresAtDate,
      // Use the student's effective open date so the schema agrees with the
      // picker's minDate (effectiveMinScheduleDate); otherwise an extended
      // student whose individual availableFrom is later than the base could
      // pass validation for a date the picker blocks.
      availableFrom:
        cubit.effectiveAssignedAtDate ?? cubit.assignment.assignedAtDate,
    }),
    tt,
    popDialog,
    setIsOpen: () => {},
    setIsLoading: () => {},
  })
  return showDatePickerDialog(props)
}

export function getScheduleRoomStateDatePickerDialogProps({
  useInlineDialog,
  scheduledAtDate,
  roomStateName,
  isOpen,
  minDate,
  maxDate,
  cubit,
  schema,
  tt,
  popDialog,
  setIsOpen,
  setIsLoading,
}: {
  useInlineDialog: boolean
  roomStateName: string
  minDate: DateTime
  maxDate: DateTime | undefined
  scheduledAtDate: DateTime | undefined
  isOpen: boolean
  cubit: StudentAssignmentCubit
  schema: ReturnType<typeof getRoomStateScheduledAtSchema>
  tt: ReturnType<typeof useTranslationTyped>['tt']
  popDialog: () => void
  setIsOpen: (open: boolean) => void
  setIsLoading: (loading: boolean) => void
}): React.ComponentProps<typeof DatePickerDialog> {
  return {
    useInlineDialog,
    transitionDuration: 0,
    value: scheduledAtDate ?? null,
    content: roomStateName,
    error: undefined,
    show: isOpen,
    onCancel: () => {
      if (useInlineDialog) return setIsOpen(false)
      popDialog()
    },
    min: minDate,
    max: maxDate,
    onChange: async (dateTime) => {
      const { error, data } = schema.safeParse(dateTime)
      if (error) {
        const msg = error?.errors?.length
          ? error.errors[0].message
          : tt.student_assignment.unexpected_error_scheduling_room()
        return toast.error(msg)
      }
      try {
        useInlineDialog ? setIsOpen(false) : popDialog()
        setIsLoading(true)
        await cubit.updateRoomScheduledTime(data)
      } catch (e) {
        toast.error(tt.student_assignment.unexpected_error_scheduling_room())
        captureException(e)
      } finally {
        setIsLoading(false)
      }
    },
  }
}
